import React, { useState, useEffect, useCallback } from 'react';
import { RadioDot } from "@/components/RadioOption";

interface CtcStatus {
  enabled: boolean;
  byokEnabled: boolean;
  prescreenEnabled: boolean;
  schedulingEnabled: boolean;
  recordingEnabled: boolean;
  geoRoutingEnabled: boolean;
  reportsEnabled: boolean;
  minutesPerMonth: number;
  configuredCallMode: string;
  isConfigured: boolean;
}

interface CtcConfig {
  siteId: number;
  callMode: string;
  byokAccountSid?: string;
  byokAccountSidMasked?: string;
  byokAuthToken?: string;
  byokFromNumber?: string;
  sipTransferUri?: string;
  hasByokCreds?: boolean;
  greetingPrompt: string;
  noAgentAvailableMessage: string;
  prescreenPrompt?: string;
  agentWhisperText?: string;
  recordingConsentEnabled: boolean;
  recordingConsentMessage: string;
  abandonedCallSmsEnabled: boolean;
  abandonedCallSmsTemplate: string;
  ringTimeoutSeconds: number;
  maxQueueDepth: number;
  showEstimatedWaitTime: boolean;
  businessHoursJson?: string;
  businessHoursTimezone: string;
  isEnabled: boolean;
}

interface CtcTeam {
  teamId: number;
  teamName: string;
  acceptClickToCalls: boolean;
  geographicAreaCodes?: string;
  routingPriority: number;
  isFallbackTeam: boolean;
  teamGreetingOverride?: string;
  maxConcurrentCalls: number;
}

interface CtcReport {
  summary: {
    total: number;
    completed: number;
    abandoned: number;
    failed: number;
    rejAbuse: number;
    scheduled: number;
    acceptRatePct: number;
    abandonRatePct: number;
    avgHandleSeconds: number;
    avgWaitSeconds: number;
    totalMinutes: number;
  };
  hourlyBreakdown: Array<{ hour: number; total: number; completed: number }>;
  dailyTrend: Array<{ date: string; total: number; completed: number }>;
  agentBreakdown: Array<{ agentId: number; calls: number; avgDuration: number }>;
}

interface QueueEntry {
  id: number;
  callerName: string;
  callerPhone: string;
  status: string;
  waitSeconds: number;
  agentId?: number;
  teamId?: number;
  pageUrl?: string;
}

const CALL_MODES = [
  { value: 'velaro_twilio', label: 'Velaro-Hosted Twilio', description: 'We handle the phone infrastructure. Minutes billed against your plan.' },
  { value: 'byok_twilio',   label: 'Bring Your Own Twilio (Enterprise)', description: 'Use your own Twilio account. You pay Twilio directly.' },
  { value: 'cti_provider',  label: 'Existing Phone Integration', description: 'Use your connected SkySwitch, RingCentral, or other CTI provider.' },
  { value: 'bridge',        label: 'Twilio Bridge', description: 'Full call bridging with transcription. Higher latency but complete conversation record.' },
  { value: 'sip_transfer',  label: 'SIP Transfer', description: 'Transfer directly to your SIP PBX. No transcription available after transfer.' },
];

const ALL_TABS = ['Overview', 'Call Flow', 'Teams', 'Limits & Hours', 'Recording', 'Reports', 'Live Queue'];

const fmtSeconds = (s: number) => {
  const sRounded = Math.round(s);
  return sRounded < 60 ? `${sRounded}s` : `${Math.floor(sRounded / 60)}m ${sRounded % 60}s`;
};

export default function ClickToCallPage() {
  const [activeTab, setActiveTab] = useState('Overview');
  const [status, setStatus] = useState<CtcStatus | null>(null);
  const [config, setConfig] = useState<CtcConfig | null>(null);
  const [teams, setTeams] = useState<CtcTeam[]>([]);
  const [report, setReport] = useState<CtcReport | null>(null);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, configRes, teamsRes] = await Promise.all([
        fetch('/api/ClickToCall/status'),
        fetch('/api/ClickToCall/config'),
        fetch('/api/ClickToCall/teams'),
      ]);
      const statusData = statusRes.ok ? await statusRes.json() : { enabled: false };
      const configData = configRes.ok ? await configRes.json() : null;
      const teamsData  = teamsRes.ok  ? await teamsRes.json()  : [];
      setStatus(statusData);
      setConfig(configData);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch {
      setError('Failed to load Click-to-Call settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try {
      const res = await fetch('/api/ClickToCall/reports');
      if (res.ok) setReport(await res.json());
    } catch { /* non-critical */ }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const res = await fetch('/api/ClickToCall/queue');
      if (res.ok) {
        const d = await res.json();
        setQueue(Array.isArray(d?.queue) ? d.queue : []);
      }
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'Reports') loadReport(); }, [activeTab, loadReport]);
  useEffect(() => {
    if (activeTab !== 'Live Queue') return;
    loadQueue();
    const id = setInterval(loadQueue, 10000);
    return () => clearInterval(id);
  }, [activeTab, loadQueue]);

  const save = async () => {
    if (!config) return;
    setSaving(true); setSaved(false); setError(null);
    try {
      const res = await fetch('/api/ClickToCall/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
      else setError('Failed to save configuration.');
    } catch { setError('Network error saving configuration.'); }
    finally { setSaving(false); }
  };

  const saveTeams = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch('/api/ClickToCall/teams', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teams),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
      else setError('Failed to save team configuration.');
    } catch { setError('Network error.'); }
    finally { setSaving(false); }
  };

  const updateConfig = (patch: Partial<CtcConfig>) => setConfig(c => c ? { ...c, ...patch } : c);
  const updateTeam   = (idx: number, patch: Partial<CtcTeam>) =>
    setTeams(ts => ts.map((t, i) => i === idx ? { ...t, ...patch } : t));

  const visibleTabs = ALL_TABS.filter(tab => {
    if (tab === 'Recording' && !status?.recordingEnabled) return false;
    if (tab === 'Reports'   && !status?.reportsEnabled)   return false;
    return true;
  });

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  if (!status?.enabled) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="text-5xl mb-4">📞</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Click-to-Call</h2>
          <p className="text-gray-600 mb-6">
            Let visitors request an immediate phone call from your website.
            Your system calls them back and bridges them to an available agent.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-8 text-left">
            {([
              ['Instant Connection', 'Visitor clicks — phone rings within 20 seconds'],
              ['Abuse Protection',   'Rate limiting + area code routing built-in'],
              ['AI Pre-screening',   'Bot qualifies the caller before you pick up'],
              ['Full Analytics',     'Accept rate, handle time, agent leaderboard'],
            ] as [string, string][]).map(([title, desc]) => (
              <div key={title} className="bg-gray-50 rounded-lg p-4">
                <div className="font-semibold text-gray-800">{title}</div>
                <div className="text-sm text-gray-600 mt-1">{desc}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            Click-to-Call requires a Pro or Enterprise plan. Contact support to upgrade.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Click-to-Call</h1>
          <p className="text-gray-500 text-sm mt-1">Visitors request a call — system dials them — agent answers</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-green-600 text-sm font-medium">Saved</span>}
          {error && <span className="text-red-600 text-sm">{error}</span>}
          {config && (
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-gray-600">Enabled</span>
              <button
                type="button"
                aria-label={config.isEnabled ? 'Disable Click-to-Call' : 'Enable Click-to-Call'}
                onClick={() => updateConfig({ isEnabled: !config.isEnabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.isEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1" aria-label="Click-to-Call configuration tabs">
          {visibleTabs.map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              {tab === 'Live Queue' && queue.length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {queue.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Overview Tab ── */}
      {activeTab === 'Overview' && config && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Call Mode',    value: CALL_MODES.find(m => m.value === config.callMode)?.label ?? config.callMode },
              { label: 'Ring Timeout', value: `${config.ringTimeoutSeconds}s` },
              { label: 'Queue Limit',  value: config.maxQueueDepth === 0 ? 'Unlimited' : `${config.maxQueueDepth} calls` },
              { label: 'Plan Minutes', value: (status?.minutesPerMonth ?? 0) === 0 ? 'Unlimited' : `${status?.minutesPerMonth}/mo` },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">{value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Call Mode</h3>
            <div className="space-y-3">
              {CALL_MODES.filter(m => !(m.value === 'byok_twilio' && !status?.byokEnabled)).map(mode => (
                <label
                  key={mode.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    config.callMode === mode.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input type="radio" name="callMode" value={mode.value} checked={config.callMode === mode.value} onChange={() => updateConfig({ callMode: mode.value })} className="sr-only" />
                  <RadioDot checked={config.callMode === mode.value} className="mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">{mode.label}</div>
                    <div className="text-sm text-gray-500">{mode.description}</div>
                    {mode.value === 'sip_transfer' && (
                      <div className="mt-1 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        SIP transfer: call is handed off to your PBX. Transcription, recording, and sentiment analysis stop at the transfer point.
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {config.callMode === 'byok_twilio' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="font-medium text-gray-800">Your Twilio Credentials</h4>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Account SID</label>
                  <input
                    type="text"
                    placeholder={config.hasByokCreds ? (config.byokAccountSidMasked ?? 'ACxxxxxxxx') : 'ACxxxxxxxx'}
                    value={config.byokAccountSid ?? ''}
                    onChange={e => updateConfig({ byokAccountSid: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Auth Token {config.hasByokCreds && '(leave blank to keep existing)'}
                  </label>
                  <input
                    type="password"
                    placeholder={config.hasByokCreds ? '••••••••' : 'your_auth_token'}
                    value={config.byokAuthToken ?? ''}
                    onChange={e => updateConfig({ byokAuthToken: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From Number (E.164)</label>
                  <input
                    type="text"
                    placeholder="+12125550100"
                    value={config.byokFromNumber ?? ''}
                    onChange={e => updateConfig({ byokFromNumber: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>
            )}

            {config.callMode === 'sip_transfer' && (
              <div className="mt-4">
                <label className="block text-xs text-gray-500 mb-1">SIP Transfer URI</label>
                <input
                  type="text"
                  placeholder="sip:pbx.yourcompany.com"
                  value={config.sipTransferUri ?? ''}
                  onChange={e => updateConfig({ sipTransferUri: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Call Flow Tab ── */}
      {activeTab === 'Call Flow' && config && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visitor Greeting <span className="text-gray-400 font-normal">(read by TTS when visitor answers)</span>
              </label>
              <textarea
                rows={3}
                value={config.greetingPrompt}
                onChange={e => updateConfig({ greetingPrompt: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">Placeholders: {'{{agentName}}'}, {'{{companyName}}'}, {'{{waitMinutes}}'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No-Agent Available Message
              </label>
              <textarea
                rows={2}
                value={config.noAgentAvailableMessage}
                onChange={e => updateConfig({ noAgentAvailableMessage: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent Whisper Text <span className="text-gray-400 font-normal">(played to agent right before they are connected)</span>
              </label>
              <textarea
                rows={2}
                value={config.agentWhisperText ?? ''}
                onChange={e => updateConfig({ agentWhisperText: e.target.value })}
                placeholder="Incoming click-to-call from {{callerName}}. Topic: {{prescreenSummary}}"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            {status?.prescreenEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Pre-screen Prompt{' '}
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full ml-1">Enterprise</span>
                </label>
                <textarea
                  rows={4}
                  value={config.prescreenPrompt ?? ''}
                  onChange={e => updateConfig({ prescreenPrompt: e.target.value })}
                  placeholder="You are a pre-screening assistant. Ask the caller their name, the reason for calling, and their account number if applicable. Keep it under 60 seconds."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  The AI will qualify the caller before connecting to an agent. The summary is passed to the agent whisper.
                </p>
              </div>
            )}
            <div className="border-t border-gray-100 pt-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-gray-700">Abandoned Call SMS Recovery</div>
                  <div className="text-xs text-gray-400">Send an automatic SMS to callers who hung up before connecting</div>
                </div>
                <button
                  type="button"
                  aria-label={config.abandonedCallSmsEnabled ? 'Disable abandoned call SMS' : 'Enable abandoned call SMS'}
                  onClick={() => updateConfig({ abandonedCallSmsEnabled: !config.abandonedCallSmsEnabled })}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${config.abandonedCallSmsEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${config.abandonedCallSmsEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              {config.abandonedCallSmsEnabled && (
                <textarea
                  rows={2}
                  value={config.abandonedCallSmsTemplate}
                  onChange={e => updateConfig({ abandonedCallSmsTemplate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                />
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Teams Tab ── */}
      {activeTab === 'Teams' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Select which teams receive Click-to-Call requests. Teams are offered calls in priority order (lower number = higher priority).
          </p>
          {teams.length === 0 && (
            <div className="text-gray-400 text-sm">No teams found. Create a team in Settings → Teams first.</div>
          )}
          <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
            {teams.map((team, idx) => (
              <div
                key={team.teamId}
                className={`bg-white border rounded-xl p-5 transition-colors ${
                  team.acceptClickToCalls ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      aria-label={`${team.acceptClickToCalls ? 'Disable' : 'Enable'} click-to-call for ${team.teamName}`}
                      onClick={() => updateTeam(idx, { acceptClickToCalls: !team.acceptClickToCalls })}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${team.acceptClickToCalls ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${team.acceptClickToCalls ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="font-medium text-gray-900">{team.teamName}</span>
                    {team.isFallbackTeam && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Fallback</span>
                    )}
                  </div>
                  {team.acceptClickToCalls && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500" htmlFor={`priority-${team.teamId}`}>Priority</label>
                      <input
                        id={`priority-${team.teamId}`}
                        type="number"
                        min={1}
                        max={999}
                        value={team.routingPriority}
                        onChange={e => updateTeam(idx, { routingPriority: parseInt(e.target.value) || 100 })}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-xs text-center"
                      />
                    </div>
                  )}
                </div>
                {team.acceptClickToCalls && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    {status?.geoRoutingEnabled && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1" htmlFor={`areacodes-${team.teamId}`}>
                          Area Codes (comma-separated)
                        </label>
                        <input
                          id={`areacodes-${team.teamId}`}
                          type="text"
                          placeholder="212, 646, 718"
                          value={team.geographicAreaCodes ?? ''}
                          onChange={e => updateTeam(idx, { geographicAreaCodes: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1" htmlFor={`maxcalls-${team.teamId}`}>
                        Max Concurrent Calls (0 = unlimited)
                      </label>
                      <input
                        id={`maxcalls-${team.teamId}`}
                        type="number"
                        min={0}
                        value={team.maxConcurrentCalls}
                        onChange={e => updateTeam(idx, { maxConcurrentCalls: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-500 mb-1" htmlFor={`greeting-${team.teamId}`}>
                        Team Greeting Override (optional)
                      </label>
                      <input
                        id={`greeting-${team.teamId}`}
                        type="text"
                        placeholder="Leave blank to use site greeting"
                        value={team.teamGreetingOverride ?? ''}
                        onChange={e => updateTeam(idx, { teamGreetingOverride: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={team.isFallbackTeam}
                          onChange={e => updateTeam(idx, { isFallbackTeam: e.target.checked })}
                        />
                        Fallback team — receives calls with no geographic match
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={saveTeams}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save Team Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ── Limits & Hours Tab ── */}
      {activeTab === 'Limits & Hours' && config && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <h3 className="font-semibold text-gray-800">Rate Limiting & Abuse Prevention</h3>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ringTimeout">
                  Ring Timeout (seconds)
                </label>
                <input
                  id="ringTimeout"
                  type="number"
                  min={10}
                  max={120}
                  value={config.ringTimeoutSeconds}
                  onChange={e => updateConfig({ ringTimeoutSeconds: parseInt(e.target.value) || 45 })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Max seconds to wait for visitor to answer before marking abandoned
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="maxQueueDepth">
                  Max Queue Depth (0 = unlimited)
                </label>
                <input
                  id="maxQueueDepth"
                  type="number"
                  min={0}
                  max={100}
                  value={config.maxQueueDepth}
                  onChange={e => updateConfig({ maxQueueDepth: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Reject new requests when more than N calls are queued</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-700">Show Estimated Wait Time</div>
                <div className="text-xs text-gray-400">Display wait estimate to visitor before they submit</div>
              </div>
              <button
                type="button"
                aria-label={config.showEstimatedWaitTime ? 'Disable estimated wait time' : 'Enable estimated wait time'}
                onClick={() => updateConfig({ showEstimatedWaitTime: !config.showEstimatedWaitTime })}
                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${config.showEstimatedWaitTime ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${config.showEstimatedWaitTime ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-800 mb-3">Business Hours</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="timezone">Timezone</label>
              <select
                id="timezone"
                value={config.businessHoursTimezone}
                onChange={e => updateConfig({ businessHoursTimezone: e.target.value })}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {[
                  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                  'America/Phoenix', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo',
                  'Australia/Sydney', 'UTC',
                ].map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <p className="text-sm text-gray-500">
              Business hours configuration is available. If no hours are configured, CTC accepts requests 24/7.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Recording Tab ── */}
      {activeTab === 'Recording' && config && status?.recordingEnabled && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Call Recording</h3>
                <p className="text-sm text-gray-500 mt-1">TCPA-compliant recording with automatic consent prompt</p>
              </div>
              <button
                type="button"
                aria-label={config.recordingConsentEnabled ? 'Disable call recording' : 'Enable call recording'}
                onClick={() => updateConfig({ recordingConsentEnabled: !config.recordingConsentEnabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.recordingConsentEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.recordingConsentEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {config.recordingConsentEnabled && (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>TCPA Notice:</strong> You must obtain consent before recording. The consent message below is played to the caller before recording starts. Pressing 2 opts out; the call continues without recording.
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="consentMessage">
                    Consent Message (TTS)
                  </label>
                  <textarea
                    id="consentMessage"
                    rows={3}
                    value={config.recordingConsentMessage}
                    onChange={e => updateConfig({ recordingConsentMessage: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Reports Tab ── */}
      {activeTab === 'Reports' && status?.reportsEnabled && (
        <div className="space-y-6">
          {!report ? (
            <div className="text-center text-gray-400 py-12">Loading report data...</div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-4">
                {[
                  { label: 'Total Calls',  value: String(report.summary.total),             color: 'text-gray-900'   },
                  { label: 'Completed',    value: String(report.summary.completed),          color: 'text-green-600'  },
                  { label: 'Abandoned',    value: String(report.summary.abandoned),          color: 'text-amber-600'  },
                  { label: 'Accept Rate',  value: `${report.summary.acceptRatePct}%`,        color: 'text-blue-600'   },
                  { label: 'Avg Handle',   value: fmtSeconds(report.summary.avgHandleSeconds), color: 'text-purple-600' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
                    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Calls by Hour of Day</h4>
                  <div className="flex items-end gap-1 h-32" aria-label="Hourly call distribution chart">
                    {Array.from({ length: 24 }, (_, h) => {
                      const d   = report.hourlyBreakdown.find(x => x.hour === h);
                      const max = Math.max(...report.hourlyBreakdown.map(x => x.total), 1);
                      const pct = d ? (d.total / max) * 100 : 0;
                      return (
                        <div
                          key={h}
                          className="flex-1 flex flex-col items-center gap-0.5"
                          title={`${h}:00 — ${d?.total ?? 0} calls`}
                        >
                          <div className="w-full bg-blue-500 rounded-t" style={{ height: `${pct}%` }} />
                          {h % 6 === 0 && <div className="text-xs text-gray-400">{h}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Key Metrics</h4>
                  <div className="space-y-3">
                    {[
                      ['Avg Wait Time',   fmtSeconds(report.summary.avgWaitSeconds)],
                      ['Abandon Rate',    `${report.summary.abandonRatePct}%`],
                      ['Total Minutes',   `${report.summary.totalMinutes} min`],
                      ['Blocked (Abuse)', String(report.summary.rejAbuse)],
                      ['Scheduled',       String(report.summary.scheduled)],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between text-sm">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-medium text-gray-900">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {report.agentBreakdown.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Agent Leaderboard</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                        <th className="pb-2">Agent ID</th>
                        <th className="pb-2">Calls Handled</th>
                        <th className="pb-2">Avg Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.agentBreakdown.map(a => (
                        <tr key={a.agentId} className="border-b border-gray-50">
                          <td className="py-2 text-gray-700">Agent #{a.agentId}</td>
                          <td className="py-2 font-medium">{a.calls}</td>
                          <td className="py-2 text-gray-600">{fmtSeconds(Math.round(a.avgDuration))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Live Queue Tab ── */}
      {activeTab === 'Live Queue' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Auto-refreshes every 10 seconds</p>
            <button type="button" onClick={loadQueue} className="text-sm text-blue-600 hover:underline">
              Refresh now
            </button>
          </div>
          {queue.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <div className="text-4xl mb-3">🎉</div>
              <div className="text-gray-500">No active calls in queue</div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {queue.map(entry => (
                <div
                  key={entry.id}
                  className={`bg-white border rounded-xl p-4 flex items-center gap-4 ${
                    entry.status === 'connected' ? 'border-green-200 bg-green-50/30' :
                    entry.status === 'ringing'   ? 'border-amber-200 bg-amber-50/30' :
                    'border-blue-200 bg-blue-50/30'
                  }`}
                >
                  <div className="text-2xl" aria-hidden="true">
                    {entry.status === 'connected' ? '✅' : entry.status === 'ringing' ? '📳' : '⏳'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{entry.callerName}</div>
                    <div className="text-sm text-gray-500">{entry.callerPhone}</div>
                    {entry.pageUrl && (
                      <div className="text-xs text-gray-400 truncate">{entry.pageUrl}</div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      entry.status === 'connected' ? 'bg-green-100 text-green-700' :
                      entry.status === 'ringing'   ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {entry.status.toUpperCase()}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {fmtSeconds(entry.waitSeconds)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
